import { gql } from "@apollo/client";
import Link from "next/link";
import { useRouter } from "next/router";
import { FC, useCallback, useEffect, useRef, useState } from "react";

import useUser from "@src/features/app/hooks/useUser";
import { MESSAGING_POLL_INTERVAL_MS } from "@src/features/messaging/new/constants";
import useIntersectionObserver from "@src/lib/hooks/useIntersectionObserver";
import CaretDownFillIcon from "@src/lib/icons/18px/caret-down-fill";
import PlusIcon from "@src/lib/icons/18px/plus";
import UsersFillIcon from "@src/lib/icons/18px/users-fill";
import Avatar from "@src/lib/ui/Avatar";
import { Button } from "@src/lib/ui/Button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@src/lib/ui/Collapsible";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@src/lib/ui/Sidebar";
import Spinner from "@src/lib/ui/Spinner";
import { cn } from "@src/lib/utils/cn";

import {
  getChatDisplayTitle,
  getChatOtherMembers,
  isOneOnOneChat,
} from "./utils";
import NewChatFlow from "./NewChatFlow";
import { useCanCreateChats } from "@src/features/messaging/MessageDialogGate";
import {
  ChatListItemFragment,
  useNewMessagingChatsQuery,
} from "./ChatsNavSection.generated";
import { useMutedChatIds } from "./useChatNotificationOverride";

export const CHATS_PAGINATION_LIMIT = 50;

const ChatsNavSection: FC = () => {
  const { user } = useUser();
  const canCreateChats = useCanCreateChats();
  const { pathname, query } = useRouter();
  const [newChatOpen, setNewChatOpen] = useState(false);
  const mutedChatIds = useMutedChatIds();

  const loadedChatsCountRef = useRef(0);
  const { data, fetchMore, loading } = useNewMessagingChatsQuery({
    variables: {
      input: { archived: { value: false } },
      pagination: {
        limit: Math.max(CHATS_PAGINATION_LIMIT, loadedChatsCountRef.current),
        offset: 0,
      },
    },
    pollInterval: MESSAGING_POLL_INTERVAL_MS,
  });

  const chats = data?.MyChats.objects || [];
  const totalChatsCount = data?.MyChats.totalCount || 0;
  const hasMoreChats = chats.length < totalChatsCount;
  const unreadChatsCount = chats.reduce(
    (sum, chat) => sum + (chat.unreadCount ?? 0),
    0,
  );

  useEffect(() => {
    loadedChatsCountRef.current = chats.length;
  }, [chats.length]);

  const loadMoreRef = useRef<HTMLDivElement>(null);
  const handleLoadMore = useCallback(() => {
    if (loading || !hasMoreChats) return;
    fetchMore({
      variables: {
        pagination: { limit: CHATS_PAGINATION_LIMIT, offset: chats.length },
      },
    });
  }, [loading, hasMoreChats, fetchMore, chats.length]);
  useIntersectionObserver({
    target: loadMoreRef,
    onIntersect: handleLoadMore,
    threshold: 0.5,
    enabled: hasMoreChats,
  });

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <Collapsible defaultOpen>
          <div className="flex items-center gap-1.5 px-2">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="group flex flex-1 items-center gap-1.5 py-1 text-xs font-medium text-gray-9 hover:text-gray-12"
              >
                My Chats
                <CaretDownFillIcon className="size-2 transition-transform group-data-[state=closed]:-rotate-90" />
              </button>
            </CollapsibleTrigger>
            {canCreateChats && (
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label="New chat"
                className="shrink-0"
                onClick={() => setNewChatOpen(true)}
              >
                <PlusIcon className="size-3.5" />
              </Button>
            )}
            {unreadChatsCount > 0 && (
              <ChatUnreadBadge count={unreadChatsCount} />
            )}
          </div>
          <CollapsibleContent>
            <SidebarMenu className="mt-1">
              {chats.map((chat) => (
                <ChatNavItem
                  key={chat.id}
                  chat={chat}
                  viewerId={user?.id}
                  muted={mutedChatIds.has(chat.id)}
                  active={
                    pathname === "/messages/my-chats/[chatId]" &&
                    query.chatId === chat.id
                  }
                />
              ))}
            </SidebarMenu>
            {hasMoreChats && (
              <div ref={loadMoreRef} className="mt-2 self-stretch">
                <Spinner style={{ margin: "auto" }} />
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </SidebarGroupContent>
      {newChatOpen && (
        <NewChatFlow open={newChatOpen} onOpenChange={setNewChatOpen} />
      )}
    </SidebarGroup>
  );
};

const ChatNavItem: FC<{
  chat: ChatListItemFragment;
  viewerId: number | undefined;
  muted: boolean;
  active: boolean;
}> = ({ chat, viewerId, muted, active }) => {
  const oneOnOne = isOneOnOneChat(chat);
  const otherMember = getChatOtherMembers(chat.members, viewerId)[0];

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={active}
        aria-current={active ? "page" : undefined}
        className={cn(muted && "text-gray-8")}
      >
        <Link
          href={{
            pathname: "/messages/my-chats/[chatId]",
            query: { chatId: chat.id },
          }}
        >
          {oneOnOne && otherMember ? (
            <Avatar person={otherMember} size={18} />
          ) : (
            <span className="flex size-[18px] shrink-0 items-center justify-center">
              <UsersFillIcon className="size-4" />
            </span>
          )}
          <span className="min-w-0 flex-1 truncate">
            {getChatDisplayTitle(chat, viewerId)}
          </span>
          {chat.unreadCount > 0 && <ChatUnreadBadge count={chat.unreadCount} />}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

const ChatUnreadBadge: FC<{ count: number }> = ({ count }) => (
  <span className="flex h-[15px] min-w-[22px] shrink-0 items-center justify-center rounded-full bg-brand px-1 text-[10px] leading-none font-semibold tracking-[-0.5px] text-white">
    {count}
  </span>
);

gql`
  query NewMessagingChats($input: ChatsInput, $pagination: PaginationInput) {
    UnreadChatsCount
    MyChats(input: $input, pagination: $pagination) {
      totalCount
      objects {
        ...ChatListItem
      }
    }
  }

  fragment ChatListItem on Chat {
    id
    chatType
    viewerIsOwner
    membersCanEditTitle
    membersCanEditMembers
    unreadCount
    title {
      en
    }
    userSelectionSet {
      id
      userSelections {
        id
        userMemberships {
          id
        }
      }
    }
    viewerArchivedAt
    latestMessageText
    latestMessageHasAttachments
    members {
      id
      name
      firstName
      deactivatedAt
      profileImage {
        ...AvatarProfileImage
      }
    }
  }
`;

export default ChatsNavSection;
